/**
 * The verdict bar: one line over both Overview views that says whether
 * anything needs a person (twin slice 1, plan W5 step 13; ideation §6).
 *
 * It renders the selector's verdict verbatim -- "All clear" is the
 * selector's to give, scoped ("Nothing needs you (robots and lifts)") and
 * only when every source read cleanly, nothing is looking fixed and no
 * open alert is unaccounted for. Open alerts the list cannot place are a
 * row of their own, "N other open alerts"; for ops the Alerts screen lets
 * in, it links there (`linkToAlerts`). The bar is 56 px tall; the Map | Building switch, the Key and
 * Back may sit on its LEFT (`start`), because a drawer is fixed over the
 * right 400 px.
 *
 * OverviewFrame mounts it (plan W5 step 14), with no `start` yet: the
 * switch and Back stay in the view's own heading, as W4 built them.
 */
import type { ReactNode, SVGProps } from 'react';
import { Link } from 'react-router-dom';
import {
  IconAlertTriangle,
  IconCheck,
  IconHelpCircle,
  IconInfo,
  IconRotateCcw,
} from '../../../design-system/icons';
import type { AttentionModel, VerdictKind } from '../../../state/attention/select';
import './attention.css';

export interface VerdictBarProps {
  model: Pick<AttentionModel, 'verdict' | 'otherOpenAlerts'>;
  /** The switch, Key and Back. */
  start?: ReactNode;
}

/**
 * "Confirming": a clock, so a verdict that only LOOKS fixed never wears
 * All clear's check mark (a glyph that means "done" beside "confirming…")
 * nor reconnecting's arrow. Drawn as the design-system icons are (24 px
 * box, 2 px stroke, currentColor); kept here because the shared icon set
 * is not this workstream's to change.
 */
function IconConfirming({ size = 22, ...rest }: SVGProps<SVGSVGElement> & { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12,6 12,12 16,14" />
    </svg>
  );
}

/** Shape carries the meaning, not colour alone. */
function Glyph({ kind }: { kind: VerdictKind }) {
  switch (kind) {
    case 'fire':
    case 'items':
      return <IconAlertTriangle size={22} />;
    case 'reconnecting':
      return <IconRotateCcw size={22} />;
    case 'cant_check':
    case 'checking':
      return <IconHelpCircle size={22} />;
    case 'other_alerts':
      return <IconInfo size={22} />;
    case 'clearing':
      return <IconConfirming size={22} />;
    case 'all_clear':
      return <IconCheck size={22} />;
  }
}

export function VerdictBar({ model, start }: VerdictBarProps) {
  const { verdict, otherOpenAlerts } = model;
  // When the verdict IS the other-alerts count, it is not said twice.
  const otherRow = otherOpenAlerts && verdict.kind !== 'other_alerts' ? otherOpenAlerts : null;
  const verdictText =
    verdict.kind === 'other_alerts' && otherOpenAlerts?.linkToAlerts ? (
      <Link className="nx-verdict-bar__link" to="/alerts">
        {verdict.text}
      </Link>
    ) : (
      verdict.text
    );
  return (
    <div className="nx-verdict-bar" data-kind={verdict.kind}>
      {start ? <div className="nx-verdict-bar__start">{start}</div> : null}
      <div className="nx-verdict-bar__verdict" role="status" aria-live="polite">
        <span className="nx-verdict-bar__glyph" aria-hidden="true">
          <Glyph kind={verdict.kind} />
        </span>
        <span className="nx-verdict-bar__text" data-testid="verdict-text">
          {verdictText}
        </span>
        {otherRow ? (
          <span className="nx-verdict-bar__other" data-testid="other-open-alerts">
            {otherRow.linkToAlerts ? (
              <Link className="nx-verdict-bar__link" to="/alerts">
                {otherRow.text}
              </Link>
            ) : (
              otherRow.text
            )}
          </span>
        ) : null}
      </div>
    </div>
  );
}
